import { QueryClientProvider } from '@tanstack/react-query'
import { lazy, Suspense, type ReactNode } from 'react'
import { ThemeProvider } from 'next-themes'
import { RouterProvider } from 'react-router'
import { TenantBrandingProvider } from '@feature/branding/hooks/useTenantBrandingContext'
import { AuthSessionBoundary } from '@feature/authentication/components/AuthSessionBoundary'
import { Toaster as SonnerToaster } from '@shared/components/ui/sonner'
import { queryClient } from './queryClient'
import { router } from './router'

interface AppProvidersProps {
  children?: ReactNode
}

const showReactQueryDevtools =
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_QUERY_DEVTOOLS === 'true'

const ReactQueryDevtools = showReactQueryDevtools
  ? lazy(async () => {
      const module = await import('@tanstack/react-query-devtools')
      return { default: module.ReactQueryDevtools }
    })
  : null

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <TenantBrandingProvider>
          <AuthSessionBoundary>
            {children || <RouterProvider router={router} />}
          </AuthSessionBoundary>
        </TenantBrandingProvider>
        <SonnerToaster richColors closeButton position="bottom-right" />
        {ReactQueryDevtools ? (
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
        ) : null}
      </ThemeProvider>
    </QueryClientProvider>
  )
}
